import React, { useState, useEffect } from "react";
import { Button, Modal, Input, Field, Message, Header, Toast, Pagination, Groupper, Table, Checkbox } from "react-frontier";
import { useBreadcrumbs, useUser } from "../hooks";
import { Employment, Location, SetLoading } from "../SiteClasses";
import { bindClick, bindFormChange } from "../SiteUtil";
import { Autocomplete, EmploymentCard, LocationMap } from "../components";
import UserAccess from "../UserAccess";
import Validator from "../Validator";
import moment from "moment";
import API from "../API";

interface EmploymentForm{
	employment_name: string;
	date_start: number;
	date_end: number;
	location: Location;
	allow_remote: boolean;
	geofence_radius: number;
	late_seconds: number;
	penalty_seconds: number;
}

const PAGE_SIZE = 5;

var Employments = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess, user } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [employments, setEmployments] = useState<Employment []>(null);
	var [searchEmployments, setSearchEmployments] = useState<Employment[]>(null);
	var [createModal, setCreateModal] = useState<boolean>(false);
	var [employmentForm, setEmploymentForm] = useState<EmploymentForm>({
		employment_name: '',
		date_start: null,
		date_end: null,
		location: null,
		allow_remote: false,
		geofence_radius: 500,
		late_seconds: 15,
		penalty_seconds: 30,
	});
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [page, setPage] = useState<number>(0);
	var [filter, setFilter] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);

	useEffect(()=>{
		setTitle('Empleos');
		get(0);
	}, []);

	var get = async(pageNumber: number)=>{
		var offset = pageNumber * PAGE_SIZE;
		setLoading(true);
		API.getEmploymentsList(PAGE_SIZE, offset).then(res=>{
			if(res.error) return setLoadError(res.message);
			setEmployments(res.data);
			setPage(pageNumber);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado al cargar los empleos. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onPageChange = (newPage: number)=>{
		get(newPage);
	};

	var search = (query: string)=>{
		setSearching(true);
		setSearchTimeout(null);
		API.searchEmployments(query).then(res=>{
			if(res.error) return setLoadError(res.message);
			setSearchEmployments(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error al buscar empleos. (SES-1)');
		}).finally(()=>{
			setSearching(false);
		});
	};

	var onSearchChange = (val: string)=>{
		setFilter(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length < 3){
			return setSearchEmployments(null);
		}
		setSearchTimeout(setTimeout(()=>{
			search(val);
		}, 500));
	};

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(employmentForm, {
			employment_name: [{
				rule: 'minLength', params: [3], label: 'Nombre de empleo'
			}],
			date_start: [{
				rule: 'number', label: 'Fecha de inicio'
			}],
			date_end: [{
				rule: 'number', label: 'Fecha de fin'
			}],
			location: [{
				rule: 'empty', label: 'Ubicación'
			}],
			geofence_radius: [{
				rule: 'number', label: 'Rango de registro'
			}],
			late_seconds: [{
				rule: 'number', label: 'Tolerancia'
			}],
			penalty_seconds: [{
				rule: 'number', label: 'Retardo'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		if(employmentForm.date_end < employmentForm.date_start)return prompts.push('La fecha de fin no puede ser menor a la fecha de inicio.');
		setLoading(true);
		
		var ds = moment.unix(employmentForm.date_start).startOf('day').unix();
		var de = moment.unix(employmentForm.date_end).endOf('day').unix();

		API.createEmployment(employmentForm.employment_name, ds, de, employmentForm.location, employmentForm.allow_remote, employmentForm.geofence_radius, employmentForm.late_seconds*60, employmentForm.penalty_seconds*60).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Empleo creado!');
			
			setEmployments([{
				employment_id: res.data.employment_id,
				employment_name: employmentForm.employment_name,
				date_start: ds,
				date_end: de,
				employee_count: 0,
				location: employmentForm.location,
				user_creator: user.user_id,
				user_creator_name: `${user.first_name} ${user.last_name}`,
				date_created: moment().unix(),
				deleted: false,
				allow_remote: employmentForm.allow_remote,
				geofence_radius: employmentForm.geofence_radius,
				late_seconds: employmentForm.late_seconds,
				penalty_seconds: employmentForm.penalty_seconds,
				city: employmentForm.location.city,
				country: employmentForm.location.country,
			}, ...employments]);

			setEmploymentForm({
				employment_name: '',
				date_start: moment().startOf('day').unix(),
				date_end: moment().endOf('day').unix(),
				location: null,
				allow_remote: false,
				geofence_radius: 500,
				late_seconds: 15,
				penalty_seconds: 30,
			});
			setCreateModal(false);
		}).catch(err=>{
			Toast.error('Hubo un error inesperado creando el empleo. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onFormChange = bindFormChange(employmentForm, setEmploymentForm);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!employments) return <Header text='Cargando datos' loading/>

	var shown_employments = searchEmployments || employments;
	var geofenceRadius = Number(employmentForm.geofence_radius);
	var mapRadius = Number.isFinite(geofenceRadius) && geofenceRadius > 0 ? geofenceRadius : null;

	return <div>
		<Groupper
			width={700}
			title={'Empleos'}
			style={{ marginTop: 20 }}
			titleRight={hasAccess(UserAccess.EMPLOYMENTS.CREATE_EMPLOYMENTS) && (
				<Button text="Agregar" iconName="plus-circle" color="black" size="small" onClick={bindClick(setCreateModal, true)}/>
			)}
		>
			<Input placeholder='Buscar...' value={filter} onChange={onSearchChange} style={{ marginBottom: 15 }} />
			<Table
				style={{ marginTop: 20 }}
				footer={(!!shown_employments && shown_employments.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={10} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={shown_employments.length >= PAGE_SIZE} />
						</div>
					</Table.Cell>
				)}
			>
				{searching || loading ? (
					<Table.Cell row className='empty' colSpan={7}>
						<Header loading text='Cargando datos' />
					</Table.Cell>
				) : (
					shown_employments.length !== 0 ? (
						shown_employments.map(e => (
							<EmploymentCard employment={e}/>
						))
					) : (
						<Table.Cell row className='empty' colSpan={6}>
							No se encontraron registros
						</Table.Cell>
					)
				)}
			</Table>
		</Groupper>
		<Modal open={createModal} onClose={bindClick(setCreateModal, false)} size="tiny">
			<Modal.Header>Agregar Empleo</Modal.Header>
			<Modal.Content>
				<Input label="Nombre" value={employmentForm.employment_name} onChange={onFormChange('employment_name')}/>
				<Field amount={2}>
					<Input 
						label='Fecha de inicio' 
						value={employmentForm.date_start} 
						onChange={onFormChange('date_start')} 
						calendar={{ date: moment().unix(), mode: 'date', format: 'DD/MM/YYYY'}} 
						style={{ width: '100%'}}
					/>
					<Input 
						label='Fecha de fin' 
						value={employmentForm.date_end} 
						onChange={onFormChange('date_end')} 
						calendar={{ date: moment().unix(), mode: 'date', format: 'DD/MM/YYYY'}} 
						style={{ width: '100%'}}
					/>
				</Field>
				<Field amount={2}>
					<Input label="Tolerancia(Minutos)" value={employmentForm.late_seconds} onChange={onFormChange('late_seconds')} />
					<Input label="Retardo(Minutos)" value={employmentForm.penalty_seconds} onChange={onFormChange('penalty_seconds')} />
				</Field>
				<Input label="Rango de registro(Metros)" value={employmentForm.geofence_radius} onChange={onFormChange('geofence_radius')} />
				<Field label="Ubicación">
					<Autocomplete
						placeholder="Ubicación"
						types={['establishment']}
						onPlaceSelected={onFormChange('location')}
					/>
				</Field>
				{employmentForm.location && (
					<LocationMap
						location={employmentForm.location}
						radius={mapRadius}
						height={200}
					/>
				)}
				<Checkbox label="Permitir trabajo remoto" checked={employmentForm.allow_remote} onChange={onFormChange('allow_remote')} style={{ marginTop: 20 }}/>
				<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cancelar" basic onClick={bindClick(setCreateModal, false)}/>
				<Button text="Guardar" color="black" onClick={submit}/>
			</Modal.Actions>
		</Modal>
	</div>
}

export default Employments;
